import { Component, EventEmitter, HostListener, Inject, OnDestroy, OnInit, Output } from "@angular/core";
import { MatDialogRef, MAT_DIALOG_DATA } from "@angular/material/dialog";
import { interval, Subscription, Observable } from "rxjs";
import { takeWhile } from "rxjs/operators";
import { ThemePalette } from "@angular/material/core";
import { ProgressBarMode } from "@angular/material/progress-bar";
import { environment } from "src/environments/environment";
import { compare } from "../../utils/values";

// in seconds
const TIME_REMAINING_RESPONSE = environment.logoutTimeRemainingResponse;

type DialogType = "ok" | "yesNo" | "submitCancel";

interface SimpleDialogButton {
  text: string;
  action?: string;
  color?: "success" | "warn";
  raised?: boolean;
  order?: number;
  callbackFunction?: () => void;
}

export interface SimpleDialogProperties {
  type?: DialogType;
  title?: string;
  message?: string;
  showCloseButton?: boolean;
  buttons?: SimpleDialogButton[];
  action?: string; // action on ok/yes/submit
  timerEnabled?: boolean;
  callbackFunction?: () => void;
}

const types: Record<DialogType, Pick<SimpleDialogProperties, "buttons">> = {
  ok: {
    buttons: [
      {
        text: "OK",
        color: "success",
        raised: true,
      },
    ],
  },
  yesNo: {
    buttons: [
      {
        text: "No",
        color: "warn",
        raised: true,
      },
      {
        text: "Yes",
        color: "success",
        raised: true,
      },
    ],
  },
  submitCancel: {
    buttons: [
      {
        text: "Cancel",
        color: "warn",
        raised: true,
      },
      {
        text: "Submit",
        color: "success",
        raised: true,
      },
    ],
  },
};

@Component({
  selector: "app-simple-dialog",
  templateUrl: "./simple-dialog.component.html",
  styleUrls: ["./simple-dialog.component.scss"],
})
export class SimpleDialogComponent implements OnInit, OnDestroy {
  @Output() timeLogout = new EventEmitter<any>();

  title: string;
  message: string;
  showCloseButton: boolean;
  timerEnabled: boolean;
  buttons: SimpleDialogButton[];
  createTimer: Observable<any>;
  percentProgressBar = 0;
  timerSubscription: Subscription;
  percentProgress = 0;

  color: ThemePalette = "warn";
  mode: ProgressBarMode = "determinate";

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: SimpleDialogProperties,
    public dialog: MatDialogRef<SimpleDialogComponent>,
  ) {
    this.timeLogout = new EventEmitter();
    this.buildDialog();
  }

  @HostListener("window:keyup.esc") onKeyUp(): void {
    this.close();
  }

  ngOnInit(): void {
    if (this.data.timerEnabled) {
      const source = interval(1000);
      this.createTimer = source.pipe(takeWhile((value) => this.isTimeoutReached(value)));
      this.timerSubscription = this.createTimer.subscribe();
    }
  }

  isTimeoutReached(value: number): boolean {
    let isTimeoutReached;
    if (value === TIME_REMAINING_RESPONSE + 1) {
      this.dialog.close();
    } else {
      this.percentProgressBar = this.calculateProgressBarPercent(value, TIME_REMAINING_RESPONSE);
      isTimeoutReached = true;
    }
    return isTimeoutReached;
  }

  close(action?: string): void {
    this.dialog.close(action);
  }

  ngOnDestroy(): void {
    this.timerSubscription?.unsubscribe();
  }

  calculateProgressBarPercent(percent: number, num: number): number {
    return (percent * 100) / num;
  }

  private buildDialog() {
    const type = types[this.data.type];
    this.title = this.data.title;
    this.message = this.data.message;
    this.showCloseButton = this.data.showCloseButton || true;
    this.timerEnabled = this.data.timerEnabled;

    this.buttons = (
      this.buttons ||
      type.buttons.map((button) => {
        const buttonText = button.text.toLowerCase();
        const okYesSubmitButtons = ["ok", "yes", "submit"];
        if (okYesSubmitButtons.includes(buttonText)) {
          button.action = this.data.action;
        }
        return button;
      })
    ).sort((a, b) => compare(a.order, b.order));
  }
}
